import { useEffect } from 'react';

import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import { generalRedirect, sendHotjarEvent } from '~globals/common';
import { getSession, getUserFavorites } from '~globals/user';
import { updateZipAndCounty } from '~redux/Actions/GlobalActions';
import {
    clearSessionAndUser,
    updateCcAction,
    updateFavorites,
    updateSavedSession,
    updateUser,
} from '~redux/Actions/UserActions';

import { useAppDispatch, useAppSelector } from './useRedux';

let loginWidgetInstance: { showWidget: (arg: object) => void };
let sessionHelper: null | {
    initIdleSessionTimer: () => void;
    continueSession: (token: string | null) => Promise<IUserData>;
} = null;

export default (ppSaveSessionId = '', alToken = '') => {
    const user = useAppSelector((state) => state.user.user);
    const ccAction = useAppSelector((state) => state.user.ccAction);
    const sessionCode = useAppSelector((state) => state.user.savedSession.sessionCode);
    const allid = useAppSelector((state) => state.global.allianceId);
    const logo = useAppSelector((state) => state.global.logo);
    const companyUrl = useAppSelector((state) => state.global.companyUrl);
    const planYear = useAppSelector((state) => state.global.planYear);

    const isSSR = typeof window === 'undefined';

    const dispatch = useAppDispatch();
    const pageData = usePageContext();
    const isSMB = ['/enroll/'].some((path) => pageData.location.pathname.includes(path));
    const config = {
        httpEndpoint: `${process.env.GATSBY_CC_GQL_URL}/graphql`,
        env: process.env.GATSBY_EH_ENV,
    };

    const LOGIN_WIDGET_URL = `${
        process.env.NODE_ENV === 'production' ? '/medicare' : ''
    }/medicare-tools/loginWidget.umd.min.js`;
    const LITE_LOGIN_WIDGET_URL = `${
        process.env.NODE_ENV === 'production' ? '/medicare' : ''
    }/medicare-tools/liteLoginWidget.umd.min.js`;

    const injectLoginWidget = (url: string, onload: () => void | Promise<void>) => {
        const s = document.createElement('script');
        s.type = 'text/javascript';
        s.defer = true;
        s.src = url;
        s.onload = onload;
        const x = document.getElementsByTagName('script')[0];
        (x.parentNode as ParentNode).insertBefore(s, x);
    };

    // eslint-disable-next-line @typescript-eslint/require-await
    const onAccountActivated = async (userData: IUserData) => {
        setLoginState(userData);
        if (sessionHelper && userData?.uuid) {
            sessionHelper.initIdleSessionTimer();
        }
    };

    const setLoginState = (userData: IUserData) => {
        if (userData?.uuid) {
            dispatch(updateUser({ isLoggedIn: true, data: userData }));
            const data = {
                allid: allid,
                planYear: planYear,
                sessionCode: userData.savedSessionCode,
            };
            getSession({ dispatch, updateSavedSession, updateZipAndCounty }, data).catch((e) => {
                console.log(e);
            });
            // redirect to homepage if user is on logout page
            if (pageData.location.pathname.includes('logout') && !isSSR) {
                generalRedirect(process.env.GATSBY_EH_ENV !== 'local' ? '/medicare' : '/', allid, isSMB);
            }
        } else {
            dispatch(updateUser({ isLoggedIn: false, data: {} }));
        }
    };

    const handleLogout = () => {
        fetch(config.httpEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                query: `
                    mutation logout {
                        logout {
                            hasError
                        }
                    }
                `,
            }),
        })
            .then((response) =>
                response.json().then((res) => {
                    if (res.data.logout.hasError) {
                        console.error('error logging out');
                    }
                    // clear session and user data
                    dispatch(clearSessionAndUser());

                    // redirect to logout page
                    const logoutUrl = `${process.env.GATSBY_SITE_URL}${
                        process.env.NODE_ENV === 'production'
                            ? `${process.env.GATSBY_PATH_PREFIX}`
                            : ''
                    }/logout`;
                    !isSSR && generalRedirect(logoutUrl, allid, isSMB);
                })
            )
            .catch((e) => {
                console.error(e);
            });
    };

    useEffect(() => {
        if (user.isLoggedIn && (sessionCode || ppSaveSessionId)) {
            getUserFavorites(sessionCode || ppSaveSessionId)
                .then((data) => {
                    dispatch(updateFavorites(data.customerProfile.plans.favoritePlans.length));
                })
                .catch((e) => {
                    console.warn(e);
                });
        }
    }, [user, sessionCode]);

    // for guest user: get favorites from session
    // signed in user's favorites are retrieved above, after sign in is verified
    useEffect(() => {
        if (!alToken && ppSaveSessionId) {
            getUserFavorites(ppSaveSessionId)
                .then((data) => {
                    dispatch(updateFavorites(data.customerProfile.plans.favoritePlans.length));
                })
                .catch((e) => {
                    console.warn(e);
                });
        }
    }, []);

    useEffect(() => {
        injectLoginWidget(LITE_LOGIN_WIDGET_URL, async () => {
            if (isSSR) return;

            if (!sessionHelper) {
                sessionHelper = new window.liteLoginWidget.UserSessionHelper(config);
            }
            const userData = await sessionHelper.continueSession(alToken);
            if (userData) {
                setLoginState(userData);
            }
            // does not work with sessionId anymore, requires profileId
            // getLoggedInUser(ppSaveSessionId, {
            //     dispatch,
            //     updateUser,
            //     updateFavorites,
            // }).catch(e => console.error(e));
        });
    }, []);

    useEffect(() => {
        if (!ccAction || isSSR) {
            return;
        }
        dispatch(updateCcAction(''));
        if (ccAction === 'LOGOUT') {
            handleLogout();
            return;
        }

        let landingPage = window.loginWidget?.PageType[ccAction];
        if (window && !window.loginWidget) {
            injectLoginWidget(LOGIN_WIDGET_URL, () => {
                landingPage = window.loginWidget.PageType[ccAction];
                const loginProps = {
                    config,
                    onAccountActivated,
                    landingPage,
                    withModal: true,
                    onLoggedIn: onAccountActivated,
                    allianceInfo: {
                        allianceId: allid,
                        details: {
                            allianceCompanyUrl: companyUrl,
                            allianceLogo: logo,
                        },
                    },
                    uiConfig: {
                        primaryCta: {
                            text: 'Back to Learn about Medicare',
                        },
                    },
                    additionalInfo: {
                        savedSessionCode: sessionCode,
                    },
                    onClose: () => null,
                };
                loginWidgetInstance = window.loginWidget.initLoginApp('#loginwidget', loginProps);

                sendHotjarEvent('CC_click');
                loginWidgetInstance.showWidget({ landingPage });
            });
            return;
        }

        sendHotjarEvent('CC_click');
        loginWidgetInstance.showWidget({ landingPage });
    }, [ccAction]);
};
